import { memo } from 'react';
import { StatusMessage, Button } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { PackageSearch, ArrowRight } from 'lucide-react';
import { useIntegrations } from '../context';
import { useIntegrationsPermissions } from '../hooks';

/**
 * Browse-All empty state (CT-4169): replaces the grid (only) when zero matches,
 * with three escape routes — Request {query} (all roles → CT-4175 modal), Clear
 * filters (restores the grid), and Build it yourself (admin-only, disabled
 * "Coming soon" — CT-3894 deferred). Curated sections above stay mounted.
 *
 * Title carries the "no match" message (with the query when one is active) and
 * the subtitle carries the sell-the-request copy — matching the prototype,
 * which leads with "No integrations match "{query}"" and only mentions the
 * request flow in the smaller body line below it.
 */
function MarketplaceEmptyState({ query, onClearFilters }) {
  const { t } = useTranslation('admin-studio');
  const { openRequestIntegration } = useIntegrations();
  const { canManageIntegrations } = useIntegrationsPermissions();

  return (
    <div className="flex flex-col items-center gap-4 py-8">
      <StatusMessage
        icon={PackageSearch}
        title={
          query
            ? t('integrations.marketplace.empty.titleQuery', { query })
            : t('integrations.marketplace.empty.title')
        }
        subtitle={
          query
            ? t('integrations.marketplace.empty.subtitleQuery', { query })
            : t('integrations.marketplace.empty.subtitle')
        }
      />
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button
          variant="primary"
          size="md"
          rightIcon={ArrowRight}
          onClick={() => openRequestIntegration({ prefillName: query })}
        >
          {query
            ? t('integrations.marketplace.empty.requestQuery', { query })
            : t('integrations.marketplace.empty.request')}
        </Button>
        <Button variant="secondary" size="md" onClick={onClearFilters}>
          {t('integrations.marketplace.empty.clearFilters')}
        </Button>
        {canManageIntegrations && (
          <Button
            variant="secondary"
            size="md"
            rightIcon={ArrowRight}
            disabled
            title={t('integrations.marketplace.promo.comingSoon')}
          >
            {t('integrations.marketplace.empty.buildYourself')}
          </Button>
        )}
      </div>
    </div>
  );
}

export default memo(MarketplaceEmptyState);
